import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { observer } from "mobx-react-lite";

import { useStoreByName } from "@web/hooks/use-stores.hooks";
import { validateToken } from "../../services/api";

import ArrowRight from "../../assets/SVGComponents/iconArrowRight";

import AddClassModal from "../components/modals/AddClassModal";
import { LoaderComponent } from "../components/core/LoaderComponent";

import ClassAccordion from "../components/accordions/ClassAccordion";
import { Button } from "@mui/material";
import { ClassDetails } from "@shared/types";
import { Add, Info } from "@mui/icons-material";
import MessageModal, { MessageModalProps, MessageModalType } from "../components/modals/MessageModal";
import FailedUsersModal from "../components/modals/FailedUsersModal";
import { FailedUser } from "@shared/types";

const ClassesManagement: React.FC = observer(() => {
  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { fetchTeacherData, schools, currentSchool, addNewClassWithStudents, setSelectedClass, currentClasses } =
    useStoreByName("teacherStore");
  const { userData } = useStoreByName("userStore");

  // States
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(
    false
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [messageModal, setMessageModal] = useState<MessageModalProps>({
    isOpen: false,
    title: '',
    message: '',
    type: undefined 
  });
  const [showFailedUsersModal, setShowFailedUsersModal] = useState(false);
  const [failedUsers, setFailedUsers] = useState<FailedUser[]>([]);

  const backHandler = () => navigate("/home");

  const handleAccordionChange =
    (panel: string, classId: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordion(isExpanded ? panel : false);
      setSelectedClass(isExpanded ? classId : null);
    };

  const handleAddClass = async (classDetails: ClassDetails) => {
    try {
      const response = await addNewClassWithStudents(classDetails);
      
      // טיפול בכשלונות
      if (response.details?.failed && response.details.failed.length > 0) {
        setFailedUsers(response.details.failed);
        setShowFailedUsersModal(true);
      }

      // הודעת הצלחה/אזהרה
      if (response?.status === 'error') {
        setMessageModal({
          isOpen: true,
          title: t('errorAddingClass'),
          message: response.message,
          type: 'error'
        });
      } else if (response?.status === 'partial') {
        setMessageModal({
          isOpen: true,
          title: t('classAddedSuccessfully'),
          message: response.message,
          type: 'info'
        });
      } else if (response?.status === 'success' || response?.teacherData) {
        setMessageModal({
          isOpen: true,
          title: t('classAddedSuccessfully'),
          message: response.message,
          type: 'success'
        });
      }
    } catch (error: any) {
      console.error("Error adding class:", error);
      setMessageModal({
        isOpen: true,
        title: t('errorAddingClass'),
        message: error?.response?.data?.message,
        type: 'error'
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    
    const initializeData = async () => {
      try {
        await validateToken();
        
        // בדיקה אם יש userData מלא - אם לא, חזרה לעמוד הבית
        if (!userData || !userData.personalInfo || !userData.personalInfo.role) {
          if (!cancelled) {
            navigate("/home");
          }
          return;
        }
        
        const userRoleId = userData.personalInfo.role.id;
        
        // בדיקה אם המשתמש לא מורה - נציג הודעת גישה
        if (userRoleId !== 1) {
          setIsLoading(false);
          return;
        }
                
        // עבור מורים - נטען נתוני בתי ספר אם חסרים
        if (schools.length === 0 && !cancelled) {
          
          // timeout להגנה מפני טעינה אינסופית
          const timeoutId = setTimeout(() => {
            if (!cancelled) {
              setIsLoading(false);
            }
          }, 10000);
          
          try {
            await fetchTeacherData();
            clearTimeout(timeoutId);
          } catch (error) {
            console.error("ClassesManagement: Error fetching teacher data:", error);
            clearTimeout(timeoutId);
          } finally {
            if (!cancelled) {
              setIsLoading(false);
            }
          }
        } else {
          setIsLoading(false);
        }
      } catch (e: any) {
        console.error(`error in validate: ${e}`);
        if (!cancelled) {
          navigate("/");
        }
      }
    };

    initializeData();

    return () => {
      cancelled = true;
    };
  }, [userData, schools.length, navigate, fetchTeacherData]);

  // בדיקת הרשאות - רק מורים יכולים לגשת לעמוד זה
  const userRoleId = userData?.personalInfo?.role?.id;
  const isTeacher = userRoleId === 1; // TEACHER

  if (isLoading) {
    return <LoaderComponent />;
  }
  
  if (!isTeacher) {
    return (
      <main className="body class-management-page">
        <section className="head-section">
          <div className="navigate">
            <ArrowRight onClick={backHandler} />
          </div>
          <div className="headers">
            <h1>{t("classesManagement")}</h1>
          </div>
        </section>
        <section className="levelOne-accordions">
          <div className="card no-classes-message translatable-content">
            <Info className="icon" />
            <h3>{t("accessDenied") || "אין הרשאה לגישה"}</h3>
            <p>{t("teachersOnlyAccess") || "עמוד ניהול הכיתות מיועד למורים בלבד. אם אתה מורה, אנא פנה למנהל המערכת לקבלת הרשאות מתאימות."}</p>
          </div>
        </section>
      </main>
    );
  }

  // קביעת בית ספר לתצוגה - מהסטור או מ-personalInfo
  const displaySchool = currentSchool || userData?.personalInfo?.school;

  // אם אין כיתות, נציג הודעה מתאימה
  const hasClasses = currentClasses && currentClasses.length > 0;

  return (
    <>
      <main className="body class-management-page">
        <section className="head-section">
          <div className="navigate">
            <ArrowRight onClick={backHandler} />
          </div>
          <div className="headers">
            <h1>{t("classesManagement")}</h1>
            <h4>
              <span>{t("schoolName")}:</span> {displaySchool?.name || t("noSchoolAssigned")}
            </h4>
            <div className="buttons">
              <Button
                size="large"
                className="add-class-button"
                variant="outlined"
                endIcon={<Add />}
                onClick={() => setIsAddClassModalOpen(true)}
                disabled={!displaySchool}
              >
                {t("addClass")}
              </Button>
            </div>
          </div>
          <AddClassModal
            isModalOpen={isAddClassModalOpen}
            setIsModalOpen={setIsAddClassModalOpen}
            handleAddClass={handleAddClass}
          />
        </section>
        <section className="levelOne-accordions">
          {hasClasses ? (
            currentClasses.map((classItem: any, index: number) => (
              <ClassAccordion
                key={classItem?.id || "class" + index}
                schoolID={displaySchool?.id || 0}
                classItem={classItem}
                expanded={
                  expandedAccordion === `panel${index}` || currentClasses.length === 1
                }
                onChange={handleAccordionChange(`panel${index}`, classItem.id)}
              />
            ))
          ) : (
            <div className="card no-classes-message translatable-content">
              <Info className="icon" />
              {schools.length === 0 ? (
                <>
                  <h3>{t("noClassesAssigned")}</h3>
                  <Trans i18nKey="classManagementInstructions" />
                </>
              ) : (
                <>
                  <h3>{t("noClassesYet")}</h3>
                  <p>{t("createFirstClass")}</p>
                  {!displaySchool && (
                    <p style={{ color: 'red', marginTop: '20px' }}>
                      {t("noSchoolAssignedContact")}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      </main>
      <FailedUsersModal
        isOpen={showFailedUsersModal}
        onClose={() => setShowFailedUsersModal(false)}
        failedUsers={failedUsers}
      />

      <MessageModal 
        isOpen={messageModal.isOpen}
        onClose={() => setMessageModal({ ...messageModal, isOpen: false })}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type as MessageModalType}
      />
    </>
  );
});

export default ClassesManagement;
